<template>
    <div class="policy-wrapper p-4 md:p-6 surface-card border-round-xl shadow-1 text-left">
        <div class="mb-5">
            <p class="text-900 mb-2">Version: 1.0</p>
            <p class="text-900 mb-2">Jurisdiction: Democratic Socialist Republic of Sri Lanka</p>
            <p class="text-600 italic font-light">
                These Terms take effect from the date you access, browse, register an account on, or otherwise use the Platform or its services.
            </p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">1. INTRODUCTION</h2>
            <p class="text-body mb-2">1.1 These Terms and Conditions (“Terms”) govern access to and use of the ILearn’d Tutors website, mobile application, and related digital services (collectively, the “Platform”) by all general users, including visitors who browse the Platform without registering an account.</p>
            <p class="text-body mb-2">1.2 By accessing, browsing, or using the Platform in any manner, you acknowledge that you have read, understood, and agree to be legally bound by these Terms, together with the Privacy Policy, Cookie Policy, and all other published Platform policies.</p>
            <p class="text-body mb-2">1.3 If you subsequently register as a Student, Tutor, or Institute, the specific terms applicable to those user categories shall apply <b>in addition</b> to these Terms.</p>
            <p class="text-body">1.4 If you do not agree with these Terms, you must immediately discontinue use of the Platform.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">2. PLATFORM OVERVIEW</h2>
            <p class="text-body mb-2">2.1 ILearn’d Tutors is an <b>educational technology platform</b> that provides, among other things:</p>
            <p class="text-body ml-3 mb-1">(a) tutor discovery and information tools;</p>
            <p class="text-body ml-3 mb-1">(b) access to educational resources and past papers (subject to access level);</p>
            <p class="text-body ml-3 mb-1">(c) account creation for students, tutors, and institutes;</p>
            <p class="text-body ml-3 mb-1">(d) scheduling, communication, and payment infrastructure; and</p>
            <p class="text-body ml-3 mb-2">(e) analytics and learning support tools.</p>
            <p class="text-body mb-2">2.2 ILearn’d Tutors acts solely as a <b>technology facilitator</b> and does not:</p>
            <p class="text-body ml-3 mb-1">(a) employ tutors;</p>
            <p class="text-body ml-3 mb-1">(b) control academic delivery; or</p>
            <p class="text-body ml-3">(c) guarantee academic outcomes, grades, or examination results.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">3. ELIGIBILITY</h2>
            <p class="text-body mb-2">3.1 To use the Platform, you must:</p>
            <p class="text-body ml-3 mb-1">(a) be at least thirteen (13) years of age;</p>
            <p class="text-body ml-3 mb-1">(b) have parental or legal guardian consent if under eighteen (18);</p>
            <p class="text-body ml-3 mb-1">(c) provide accurate and truthful information where required; and</p>
            <p class="text-body ml-3 mb-2">(d) use the Platform only for lawful purposes.</p>
            <p class="text-body">3.2 ILearn’d Tutors reserves the right to restrict, suspend, or terminate access for any user who fails to meet eligibility requirements.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">4. GENERAL USER CONDUCT</h2>
            <p class="text-body mb-2">4.1 Users shall not misuse the Platform, including by:</p>
            <p class="text-body ml-3 mb-1">(a) attempting to hack, disrupt, or damage Platform systems;</p>
            <p class="text-body ml-3 mb-1">(b) uploading malware, viruses, or harmful code;</p>
            <p class="text-body ml-3 mb-2">(c) scraping, extracting, or harvesting data without permission.</p>
            <p class="text-body mb-2">4.2 Users shall not:</p>
            <p class="text-body ml-3 mb-1">(a) create false or misleading accounts;</p>
            <p class="text-body ml-3 mb-1">(b) harass, abuse, threaten, or impersonate other users;</p>
            <p class="text-body ml-3 mb-1">(c) promote illegal, unethical, or harmful activities;</p>
            <p class="text-body ml-3 mb-1">(d) interfere with bookings, payments, or Platform operations;</p>
            <p class="text-body ml-3 mb-1">(e) engage in off-platform transactions intended to bypass fees; or</p>
            <p class="text-body ml-3">(f) use Platform content, including past papers, for commercial purposes without authorisation.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">5. ACCOUNT CREATION (OPTIONAL)</h2>
            <p class="text-body mb-2">5.1 General Users may browse the Platform without creating an account; however, certain features require registration.</p>
            <p class="text-body mb-2">5.2 Where an account is created, users shall:</p>
            <p class="text-body ml-3 mb-1">(a) provide accurate and up-to-date information;</p>
            <p class="text-body ml-3 mb-1">(b) maintain confidentiality of login credentials; and</p>
            <p class="text-body ml-3 mb-2">(a) notify ILearn’d Tutors immediately of any unauthorised access.</p>
            <p class="text-body">5.3 Users are responsible for all activity conducted under their account.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">6. ACCESS TO CONTENT & INTELLECTUAL PROPERTY</h2>
            <p class="text-body mb-2">6.1 The Platform may display tutor profiles, educational articles, pricing information, class listings, and academic resources.</p>
            <p class="text-body mb-2">6.2 Content is provided for <b>personal, non-commercial use only.</b></p>
            <p class="text-body mb-2">6.3 Users shall not copy, download, reproduce, distribute, sell, or modify Platform content without prior written permission.</p>
            <p class="text-body">6.4 All intellectual property rights remain vested in ILearn’d Tutors or its licensors.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">7. PAST PAPERS & EDUCATIONAL RESOURCES</h2>
            <p class="text-body mb-2">7.1 Access to past papers and academic materials is subject to user role and access level.</p>
            <p class="text-body mb-2">7.2 Full access to past papers generally requires registration as a Student or an active subscription.</p>
            <p class="text-body mb-2">7.3 Users shall not:</p>
            <p class="text-body ml-3 mb-1">(a) share or distribute past papers externally;</p>
            <p class="text-body ml-3 mb-1">(b) upload copyrighted exam materials without proper rights;</p>
            <p class="text-body ml-3 mb-2">(c) sell, publish, or commercialise Platform resources.</p>
            <p class="text-body">7.4 Misuse may result in account suspension, termination, or legal action.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">8. PAYMENTS & PURCHASES</h2>
            <p class="text-body mb-2">8.1 Where General Users make purchases (including subscriptions or premium features), all payments must be processed <b>exclusively through the Platform’s authorised payment gateways.</b></p>
            <p class="text-body mb-2">8.2 Prices, features, and availability may change from time to time and shall be displayed at the point of purchase.</p>
            <p class="text-body mb-2">8.3 Refunds, where applicable, shall be governed by the <b>Refund & Dispute Resolution Policy</b> relevant to the specific service purchased.</p>
            <p class="text-body">8.4 Off-platform payments are strictly prohibited.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">9. USER-GENERATED CONTENT</h2>
            <p class="text-body mb-2">9.1 Users may submit reviews, feedback, comments, or support messages.</p>
            <p class="text-body mb-2">9.2 Submitted content must be truthful, respectful, and lawful.</p>
            <p class="text-body mb-2">9.3 ILearn’d Tutors reserves the right to moderate, remove, or restrict content at its discretion.</p>
            <p class="text-body">9.4 By submitting content, users grant ILearn’d Tutors a <b>non-exclusive, royalty-free licence</b> to use, display, and reproduce such content for Platform-related purposes.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">10. CHILD PROTECTION & SAFETY</h2>
            <p class="text-body mb-2">10.1 ILearn’d Tutors enforces strict child protection and safeguarding standards.</p>
            <p class="text-body mb-2">10.2 Any suspicion of harmful or inappropriate behaviour involving minors must be reported immediately through the Platform or SOS features.</p>
            <p class="text-body">10.3 Any attempt to contact minors for non-educational purposes is strictly prohibited and may be reported to relevant authorities.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">11. DATA PROTECTION & PRIVACY</h2>
            <p class="text-body mb-2">11.1 Personal data is processed in accordance with the <b>Personal Data Protection Act, No. 9 of 2022 of Sri Lanka (PDPA).</b></p>
            <p class="text-body mb-2">11.2 Use of the Platform constitutes acknowledgement of the Privacy Policy and Cookie Policy.</p>
            <p class="text-body">11.3 Users shall not misuse, harvest, or unlawfully process personal data of others.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">12. SERVICE AVAILABILITY</h2>
            <p class="text-body mb-2">12.1 ILearn’d Tutors aims to maintain Platform availability but does not guarantee uninterrupted access.</p>
            <p class="text-body mb-2">12.2 Temporary disruptions may occur due to maintenance, updates, or external factors.</p>
            <p class="text-body">12.3 ILearn’d Tutors is not responsible for internet outages, device failures, or third-party service interruptions.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">13. LIMITATION OF LIABILITY</h2>
            <p class="text-body mb-2">13.1 To the maximum extent permitted by law, ILearn’d Tutors shall not be liable for:</p>
            <p class="text-body ml-3 mb-1">(a) reliance on Platform information;</p>
            <p class="text-body ml-3 mb-1">(b) disputes between users;</p>
            <p class="text-body ml-3 mb-1">(c) tutor or student conduct;</p>
            <p class="text-body ml-3 mb-1">(d) off-Platform interactions; or</p>
            <p class="text-body ml-3 mb-2">(e) indirect or consequential losses.</p>
            <p class="text-body">13.2 To the maximum extent permitted by law, the total aggregate liability of ILearn’d Tutors arising out of or in connection with the Platform shall not exceed the total fees paid by the user to ILearn’d Tutors during the six (6) months preceding the event giving rise to the claim.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">14. TERMINATION OF ACCESS</h2>
            <p class="text-body mb-2">14.1 ILearn’d Tutors may suspend or terminate access due to policy violations, safety concerns, misuse, legal requirements, or operational needs.</p>
            <p class="text-body">14.2 Users may discontinue use of the Platform or delete their accounts at any time.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">15. GOVERNING LAW & JURISDICTION</h2>
            <p class="text-body mb-2">These Terms shall be governed by and construed in accordance with the laws of the <b>Democratic Socialist Republic of Sri Lanka.</b></p>
            <p class="text-body">Any dispute arising out of or in connection with these Terms shall be subject to the <b>jurisdiction of the courts of Sri Lanka.</b></p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">16. AMENDMENTS</h2>
            <p class="text-body mb-2">16.1 ILearn’d Tutors may update these Terms from time to time.</p>
            <p class="text-body">16.2 Material changes shall be notified through the Platform and, where required by law, shall require <b>explicit user consent</b> prior to taking effect.</p>
        </div>

        <div class="policy-section">
            <h2 class="H5-SemiBold text-primary mb-3">17. CONTACT INFORMATION</h2>
            <div class="p-3 border-round bg-faded">
                <p class="text-body font-bold mb-1"><b>ILearn’d Tutors – Legal Team</b></p>
                <p class="text-body mb-1">Email: support@ilearndsl.com</p>
                <p class="text-body mb-0">Address: 306/1 Highlevel Road, Colombo 06, Sri Lanka</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.text-primary { color: #1D97BD !important; }
.bg-faded { background-color: rgba(29, 151, 189, 0.04); }
.text-body {
    font-size: 0.95rem;
    color: #4A5568;
    font-weight: 400;
    line-height: 1.6;
}
.ml-3 { margin-left: 1.5rem; }
</style>